import {BillingPlanPanel} from '../billing-page/billing-plan-panel';
import {Trans} from '@ui/i18n/trans';
import {Button} from '@ui/buttons/button';
import {CheckCircleIcon} from '@ui/icons/material/CheckCircle';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {useEffect, useState, useCallback} from 'react';
import {apiClient} from '@common/http/query-client';
import {useSearchParams} from 'react-router-dom';
import {useCartStore} from '../shop/use-cart-store';

export function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationError, setVerificationError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const clearCart = useCartStore((state: any) => state.clearCart);

  const sessionId = searchParams.get('sessionid');

  const verifyPayment = useCallback(async (sessionId: string) => {
    try {
      // Generate token for payment verification
      const tokenResponse = await apiClient.post('drive/generate-token', {
        endpoint_type: 'products',
      });
      
      const token = tokenResponse.data.token;

      // Call external API to verify payment
      const response = await fetch('https://apcrosa.syscomlive.co.uk/api/v1/drive/payments/validate/'+sessionId, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token,
        },
        body: JSON.stringify({
          for_drive: true,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to verify payment');
      }

      const result = await response.json();
      
      if (result.status === 1) {
        setIsVerified(true);
        setPaymentInfo(result.data); // Store payment info
        clearCart(); // Clear cart after successful verification
      } else {
        throw new Error(result.msg || 'Payment verification failed');
      }
    } catch (error) {
      setVerificationError('Failed to verify payment. Please contact support.');
    } finally {
      setIsVerifying(false);
    }
  }, [clearCart]);

  useEffect(() => {
    if (sessionId) {
      verifyPayment(sessionId);
    } else {
      setIsVerifying(false);
      setVerificationError('No session ID provided');
    }
  }, [sessionId, verifyPayment]);

  if (isVerifying) {
    return (
      <div className="container mx-auto p-0 md:p-24 max-w-1280">
        <BillingPlanPanel title={<Trans message="Verifying Payment" />}>
          <div className="text-center py-32">
            <div className="flex justify-center mb-16">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent"></div>
            </div>
            <p className="text-muted">
              <Trans message="Please wait while we verify your payment..." />
            </p>
          </div>
        </BillingPlanPanel>
      </div>
    );
  }

  if (verificationError) {
    return (
      <div className="container mx-auto p-0 md:p-24 max-w-1280">
        <BillingPlanPanel title={<Trans message="Payment Verification Failed" />}>
          <div className="text-center py-32">
            <div className="flex justify-center mb-16">
              <div className="w-64 h-64 bg-danger/10 rounded-full flex items-center justify-center">
                <CheckCircleIcon size="lg" className="text-danger" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-danger mb-8">
              <Trans message="Payment Verification Failed" />
            </h2>
            <p className="text-muted mb-24 max-w-md mx-auto">
              {verificationError}
            </p>

            {/* Payment Information for Failed Payment */}
            <div className="mb-24 max-w-md mx-auto">
              <h3 className="text-lg font-semibold mb-16">
                <Trans message="Payment Details" />
              </h3>
              <div className="border border-divider rounded-lg overflow-hidden">
                <table className="w-full">
                  <tbody>
                    <tr className="border-b border-divider">
                      <td className="px-16 py-8 font-medium bg-muted/20">
                        <Trans message="Payment Status" />
                      </td>
                      <td className="px-16 py-8 text-danger">Failed</td>
                    </tr>
                    <tr className="border-b border-divider">
                      <td className="px-16 py-8 font-medium bg-muted/20">
                        <Trans message="Session ID" />
                      </td>
                      <td className="px-16 py-8">{sessionId}</td>
                    </tr>
                    <tr>
                      <td className="px-16 py-8 font-medium bg-muted/20">
                        <Trans message="Date" />
                      </td>
                      <td className="px-16 py-8">{new Date().toLocaleDateString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-12 justify-center">
              <Button
                variant="flat"
                color="primary"
                onClick={() => navigate('/my-settings/licenses')}
              >
                <Trans message="View My Licenses" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/shop')}
              >
                <Trans message="Continue Shopping" />
              </Button>
            </div>
          </div>
        </BillingPlanPanel>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-0 md:p-24 max-w-1280">
      <BillingPlanPanel title={<Trans message="Payment Successful" />}>
        <div className="text-center py-32">
          {/* Success Icon */}
          <div className="flex justify-center mb-16">
            <div className="w-64 h-64 bg-positive/10 rounded-full flex items-center justify-center">
              <CheckCircleIcon size="lg" className="text-positive" />
            </div>
          </div>

          {/* Success Message */}
          <h2 className="text-2xl font-bold text-success mb-8">
            <Trans message="Payment Completed Successfully!" />
          </h2>

          <p className="text-muted mb-24 max-w-md mx-auto">
            <Trans message="Thank you for your purchase. Your payment has been processed successfully and your license keys have been activated." />
          </p>

          {/* Payment Information */}
          {paymentInfo && (
            <div className="mb-24 max-w-md mx-auto">
              <h3 className="text-lg font-semibold mb-16">
                <Trans message="Payment Details" />
              </h3>
              <div className="border border-divider rounded-lg overflow-hidden">
                <table className="w-full">
                  <tbody>
                    <tr className="border-b border-divider">
                      <td className="px-16 py-8 font-medium bg-muted/20">
                        <Trans message="Payment Status" />
                      </td>
                      <td className="px-16 py-8">{paymentInfo?.status}</td>
                    </tr>
                    <tr className="border-b border-divider">
                      <td className="px-16 py-8 font-medium bg-muted/20">
                        <Trans message="Payment ID" />
                      </td>
                      <td className="px-16 py-8">{paymentInfo?.id}</td>
                    </tr>
                    <tr className="border-b border-divider">
                      <td className="px-16 py-8 font-medium bg-muted/20">
                        <Trans message="Payment Date" />
                      </td>
                      <td className="px-16 py-8">{paymentInfo?.date}</td>
                    </tr>
                    <tr className="border-b border-divider">
                      <td className="px-16 py-8 font-medium bg-muted/20">
                        <Trans message="Payment Currency" />
                      </td>
                      <td className="px-16 py-8">{paymentInfo?.currency}</td>
                    </tr>
                    <tr>
                      <td className="px-16 py-8 font-medium bg-muted/20">
                        <Trans message="Payment Amount" />
                      </td>
                      <td className="px-16 py-8">{paymentInfo?.amount}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-12 justify-center">
            <Button
              variant="flat"
              color="primary"
              onClick={() => navigate('/my-settings/licenses')}
            >
              <Trans message="View My Licenses" />
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate('/shop')}
            >
              <Trans message="Continue Shopping" />
            </Button>
          </div>
        </div>
      </BillingPlanPanel>
    </div>
  );
}